import { Injectable } from '@angular/core';

import { QueryOptionsAlone } from 'apollo-angular/types';
import { Observable, map, tap } from 'rxjs';

import {
  Address,
  AddressType,
  CaseType,
  CommonStoreConfiguration,
  Customer,
  Employee,
  FormType
} from '@hdms/shared/common-types';
import { CCMSV2, GQL_DATASOURCE_VERSION_HEADER, getAccount, getLocalization } from '@hdms/shared/data-access';
import { EnvironmentService } from '@hdms/shared/util-core';

import {
  AddressCreateConfigGqlService,
  AddressCreateConfigurationResponse,
  QueryAddressCreateConfigurationDetailArgs
} from './address-create-config-gql.service';
import { CreateFormConfig } from './types';

export function getCompanyAddress(loggedInContext: Customer | Employee): Address {
  return getAccount(loggedInContext).address;
}

@Injectable({
  providedIn: 'root'
})
export class AddressCreateConfigService {
  public addressCheckEnabled: boolean;
  public addressCheckCaseType: CaseType;

  constructor(
    private readonly formConfigService: AddressCreateConfigGqlService,
    private readonly envService: EnvironmentService
  ) {}

  getConfig$(type: AddressType, addCCMSv2Header = false): Observable<CreateFormConfig> {
    const isContact = type === AddressType.CONTACT;
    const formConfigurationQueryVariables: QueryAddressCreateConfigurationDetailArgs = {
      localization: getLocalization(this.envService),
      type: isContact ? FormType.NEW_CONTACT : FormType.ADDRESS,
      isAddress: !isContact
    };

    return this.formConfigService
      .fetch(formConfigurationQueryVariables, this.getFormConfigQueryOptions(addCCMSv2Header))
      .pipe(
        map(response => response.data),
        tap(({ storeConfiguration }) => this.processStoreConfig(storeConfiguration?.common)),
        map(this.applyFields)
      );
  }

  protected applyFields({
    formConfiguration: { fields },
    loggedInContext
  }: AddressCreateConfigurationResponse): CreateFormConfig {
    return {
      fields,
      initialValues: getCompanyAddress(loggedInContext)
    };
  }

  private processStoreConfig({ addressCheck, addressCheckCaseType }: CommonStoreConfiguration = {}): void {
    this.addressCheckEnabled = !!addressCheck;
    this.addressCheckCaseType = addressCheckCaseType || CaseType.UPPERCASE;
  }

  private getFormConfigQueryOptions<TData, TVariables>(addCCMSv2Header: boolean): QueryOptionsAlone<TData, TVariables> {
    return addCCMSv2Header
      ? {
          context: {
            headers: {
              [GQL_DATASOURCE_VERSION_HEADER]: CCMSV2
            }
          }
        }
      : {};
  }
}
